<!-- eslint-disable vue/no-v-html -->
<template>
  <div
    class="
      relative mx-auto -mt-12 md:mt-0 lg:mt-4 lg:ml-auto lg:mr-0 xl:mt-4
      h-[350px] max-w-[350px]
      md:h-[250px] md:w-full
      lg:h-[350px] lg:max-w-[350px]
    "
  >
    <FocalImage
      :image="images[0]"
      :img-class="imageClass1"
      :img-style="imageStyle1"
      :srcset="images[0]?.srcset"
      :sizes="images[0]?.sizes"
      loading="eager"
      fetchpriority="high"
    />
    <FocalImage
      :image="images[1]"
      :img-class="imageClass2"
      :img-style="imageStyle2"
      :srcset="images[1]?.srcset"
      :sizes="images[1]?.sizes"
    />
    <FocalImage
      :image="images[2]"
      :img-class="imageClass3"
      :img-style="imageStyle3"
      :srcset="images[2]?.srcset"
      :sizes="images[2]?.sizes"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onBeforeMount } from 'vue';
import { useScrollPosition } from '@/vue/composables/useScrollPosition';
import type { ImageRendition } from '@/ts/types/focal-point';

interface HeroCollage {
  uuid?: string;
  images?: ImageRendition[] | null;
}

const imageClass = "absolute h-full w-auto overflow-visible";
const imageClass1 = `${imageClass} delay-600 animate-[float1_800ms_ease_forwards]`;
const imageClass2 = `${imageClass} delay-400 animate-[float2_750ms_ease_forwards]`;
const imageClass3 = `${imageClass} animate-[float3_700ms_ease_forwards]`;

// Define props with type and default values using withDefaults
const props = withDefaults(defineProps<HeroCollage>(), {
  uuid: '',
  images: () => []
});

// Grab scroll position from composable
const { y } = useScrollPosition();
/**
 * Parallax movement— adjust the individual multipliers to increase or decrease the effect.
 */
const imageStyle1 = computed(() => {
  if (y.value < 1000) { // don't calculate past a certain scroll point
    const translateY = y.value*0.087;
    return `transform: translateY(${translateY}px); transition: transform 0.01s;`;
  } else {
    return
  }
})
const imageStyle2 = computed(() => {
  if (y.value < 1000) { // don't calculate past a certain scroll point
    const translateX = -y.value*0.02; // move slightly left
    const translateY = y.value*0.075;
    return `transform: translate(${translateX}px, ${translateY}px); transition: transform 0.01s;`;
  } else {
    return
  }
})
const imageStyle3 = computed(() => {
  if (y.value < 1000) { // don't calculate past a certain scroll point
    const translateX = y.value*0.02; // move slightly right
    const translateY = y.value*0.068;
    return `transform: translate(${translateX}px, ${translateY}px); transition: transform 0.01s;`;
  } else {
    return
  }
})

const rootElement = ref(null);

onBeforeMount(() => {
  rootElement.value = (document.querySelector(`[data-uuid="${props.uuid}"]`) as HTMLElement);
  delete rootElement.value.dataset['json']
});
</script>

<style>
@keyframes float1 {
  0% {
    top: 30%;
    opacity: 0;
  }
  100% {
    top: 0%;
    opacity: 1;
  }
}
@keyframes float2 {
  0% {
    top: 35%;
    opacity: 0;
  }
  100% {
    top: 0%;
    opacity: 1;
  }
}
@keyframes float3 {
  0% {
    top: 40%;
    opacity: 0;
  }
  100% {
    top: 0%;
    opacity: 1;
  }
}
</style>
